import { toast } from "@fla/components";
import axios from "axios";
import { Timestamp } from "firebase/firestore";
import { SliceStatus } from "interfaces";
import { isPlainObject } from "is-plain-object";
import { UseFormSetError } from "react-hook-form";

export const isMobile = (() => {
  if (typeof navigator === "undefined" || typeof navigator.userAgent !== "string") {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

export function removeUndefineds<T>(obj: T): T {
  if (!isPlainObject(obj)) return obj;

  const target: { [name: string]: any } = {};
  const objKeys = Object.keys(obj as any);
  for (const key of objKeys) {
    const val = (obj as any)[key];
    if (typeof val !== "undefined") {
      target[key] = removeUndefineds(val);
    }
  }

  return target as T;
}

export const copyToClipboard = async (txt: string) => {
  try {
    const textField = document.createElement("textarea");
    textField.innerText = txt;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand("copy");
    textField.remove();
  } catch (err) {
    console.log(err);
  }
};

export const range = (start: number, end: number): number[] =>
  Array.from({ length: end - start }, (_v, k) => k + start);

export const unwrapFirebaseAuthError = (
  error: any,
  setError: UseFormSetError<{
    email: string;
    password: string;
  }>
) => {
  if (error.code === "auth/user-not-found") {
    setError("email", { message: "User not found" });
  } else if (error.code === "auth/wrong-password") {
    setError("password", { message: "Wrong Password, Please try again" });
  } else if (error.code === "auth/email-already-in-use") {
    setError("email", { message: "This account already exists" });
  } else if (error.code === "auth/too-many-requests") {
    setError("email", { message: "Too many requests, Please try again later" });
  } else if (error.code === "auth/invalid-email") {
    setError("email", { message: "Invalid email" });
  } else if (error.code === "auth/invalid-login-credentials") {
    setError("email", { message: "Invalid credentials" });
  } else if (error.code === "auth/network-request-failed") {
    setError("email", { message: "Network request failed" });
  } else if (error.code === "auth/weak-password") {
    setError("password", { message: "Weak password, please try a stronger one" });
  } else if (error.code === "auth/expired-action-code") {
    setError("email", { message: "Expired action code, please request a new one" });
  } else if (error.code === "auth/invalid-action-code") {
    setError("email", { message: "Invalid action code" });
  } else if (error.code === "auth/operation-not-allowed") {
    setError("email", { message: "Operation not allowed, please contact support" });
  } else if (error.code === "auth/invalid-verification-code") {
    setError("email", { message: "Invalid verification code" });
  } else if (error.code === "auth/invalid-verification-id") {
    setError("email", { message: "Invalid verification ID" });
  } else if (error.code === "auth/captcha-check-failed") {
    setError("email", { message: "Captcha check failed, please try again" });
  } else if (error.code === "auth/user-disabled") {
    setError("email", { message: "User account is disabled" });
  } else if (error.code === "auth/provider-already-linked") {
    setError("email", { message: "Provider is already linked to this account" });
  } else {
    setError("email", { message: error?.message ?? "Something went wrong!" });
  }
};

export const unwrapAPIError = (error: any): string => {
  if (axios.isCancel(error)) {
    return "";
  }
  let errorValue = error?.data || error?.statusText;
  if (!error?.data && error?.message) {
    errorValue = error.message;
  }
  if (errorValue?.message) {
    errorValue = errorValue.message;
  }
  if (errorValue?.[0] && errorValue?.[0]?.message) {
    errorValue = errorValue[0].message;
  }
  if (error?.response?.statusText) {
    errorValue = error?.response?.statusText;
  }

  return typeof errorValue === "string" ? errorValue : "An error occurred.";
};

export const convertDateObjToISO = (date: { day: number; month: number; year: number }): string => {
  const day = String(date.day).length === 1 ? `0${date.day}` : date.day;
  const month = String(date.month).length === 1 ? `0${date.month}` : date.month;
  const year = date.year;

  const isoDate = new Date(`${year}-${month}-${day}`).toISOString();
  return isoDate;
};

export const convertISOToDateObj = (
  date: string
): {
  day: number;
  month: number;
  year: number;
} => {
  const year = new Date(date).getFullYear();
  const month = new Date(date).getMonth() + 1;
  const day = new Date(date).getDate();

  return { day, month, year };
};

export const componentLoader = (lazyComponent: () => any, attemptsLeft = 3): Promise<any> => {
  return new Promise((resolve, reject) => {
    lazyComponent()
      .then(resolve)
      .catch((error: any) => {
        // let us retry after 1500 ms
        setTimeout(() => {
          if (attemptsLeft === 1) {
            reject(error);
            return;
          }
          componentLoader(lazyComponent, attemptsLeft - 1).then(resolve, reject);
        }, 300);
      });
  });
};

/**
 * @description
 * Takes an Array<V>, and a grouping function,
 * and returns a Map of the array grouped by the grouping function.
 *
 * @param list An array of type V.
 * @param keyGetter A Function that takes the the Array type V as an input, and returns a value of type K.
 *                  K is generally intended to be a property key of V.
 *
 * @returns Map of the array grouped by the grouping function.
 */
//export function groupBy<K, V>(list: Array<V>, keyGetter: (input: V) => K): Map<K, Array<V>> {
//    const map = new Map<K, Array<V>>();
export function groupBy<K, V>(list: Array<V>, keyGetter: (item: V) => K): Map<K, Array<V>> {
  const map = new Map();
  list.forEach(item => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export const firestoreQueryTimestamp = (day: number) => {
  const date = new Date();
  date.setDate(date.getDate() - day);

  return {
    from: Timestamp.fromDate(date),
    to: Timestamp.fromDate(new Date())
  };
};

export const removeEmpty = (obj: { [k: string]: any }) => {
  const newObj = {} as any;
  Object.keys(obj).forEach(key => {
    if (obj[key] === Object(obj[key])) newObj[key] = removeEmpty(obj[key]);
    else if (obj[key] !== undefined) newObj[key] = obj[key];
  });
  return newObj;
};

export const parseToMarkDown = (str: string): string => {
  if (str.match(/\\n/g)) str = str.replace(/\\n/g, "\n");

  str = str.trim();

  if (str.match(/\s\([ivx]+\)/g)) {
    const regex = /\s\([ivx]+\)/g;
    str = str.replace(regex, "\n\n$&");
    return str;
  }

  if (str.match(/\s([a-z])\)\s([a-z])*/g)) {
    const regex = /\s([a-z])\)\s*/g;
    const replacement = "\n\n$1) ";
    str = str.replace(regex, replacement);
    return str;
  }

  if (str.match(/([a-z])\n\s\s\(([a-z])\)\s[a-zA-Z]/g)) {
    const regex = /\s\([a-z]\)\s(?=[a-zA-Z])/g;
    const replacement = "\n\n$&";
    str = str.replace(regex, replacement);
    return str;
  }

  return str;
};

/**
 *
 * @param path path of file
 * @param filename file name
 * @param setStatus function to set the download status
 * @param type mime-type
 */
export const downloadFile = async (
  path: string,
  filename: string,
  setStatus?: React.Dispatch<React.SetStateAction<SliceStatus>>,
  type?: string
) => {
  let toastId: any;

  try {
    if (typeof path !== "string") throw new Error("file path missing");

    if (setStatus) {
      setStatus(SliceStatus.Pending);
    } else {
      toastId = toast({ message: `Downloading Recording: ${filename}`, type: "promise" });
    }

    const res = await fetch(path);
    const data = await res.blob();

    const contentType = type || res.headers.get("Content-Type") || undefined;

    const blob = new Blob([data], { type: contentType });
    // Create blob link to download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    // Append to html link element page
    document.body.appendChild(link);
    // Start download
    link.click();
    // Clean up and remove the link
    link.parentNode?.removeChild(link);
    if (setStatus) {
      setStatus(SliceStatus.Resolved);
    } else {
      toast({
        message: `Recording Downloaded Successfully: ${filename}`,
        position: "top-center",
        toastId,
        type: "promise_update"
      });
      // setTimeout(() => {
      //   toast.dismiss(toastId);
      // }, 2000);
    }
  } catch (err) {
    console.log("err", err);

    if (setStatus) {
      setStatus(SliceStatus.Rejected);
    } else {
      toastId = toast({ message: `Recording Downloading Failed: ${filename}`, toastId, type: "error" });
      setTimeout(() => {
        toast({ toastId, type: "dismiss" });
      }, 2000);
    }
    throw new Error();
  }
};

export const convertTimeInMMSS = (audioCurrentTime: number): string => {
  const minutes: string = `0${Math.floor(audioCurrentTime / 60)}`.slice(-2);
  const seconds: string = `0${Math.floor(audioCurrentTime - Number(minutes) * 60)}`.slice(-2);
  return `${minutes}:${seconds}`;
};

export function secondsToHms(d: number) {
  d = Number(d);
  var h = Math.floor(d / 3600);
  var m = Math.floor((d % 3600) / 60);
  var s = Math.floor((d % 3600) % 60);

  var hDisplay = h > 0 ? h + (h === 1 ? " hr, " : " hrs, ") : "";
  var mDisplay = m > 0 ? m + (m === 1 ? " min, " : " mins, ") : "";
  var sDisplay = s > 0 ? s + (s === 1 ? " sec" : " secs") : "";

  let str = "";
  if (hDisplay) str += hDisplay;
  if (mDisplay) str += mDisplay;
  if (sDisplay) str += sDisplay;

  return str.replaceAll(",", "");
}

export const convertTimestamp = (timestamp: Timestamp) => {
  if (!timestamp) return "";

  let date = new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000);
  let mm = date.getMonth() + 1;
  let dd = date.getDate();
  let yyyy = date.getFullYear();

  return dd + "/" + mm + "/" + yyyy;
};

export const getRedirectUrl = () => {
  if (process.env.NODE_ENV === "production") {
    return {
      website: "https://www.splitwise.com.au/"
    };
  }
  // @ts-ignore
  else if (process.env.NODE_ENV === "staging") {
    return {
      website: "https://splitwise-app-staging.netlify.app/"
    };
  } else {
    return {
      website: "http://localhost:3000/login"
    };
  }
};
